import { cloneDeep, get } from 'lodash';
import { Nullable, Nullish } from '../types/typeish.type';

/**
 * Various functions to manipulate objects
 */
export class ObjHlp {
  /**
   * Reads field of object if exists, because making ?[] operator is to damn hard for typescript.
   * @param {T | Nullish} x
   * @param {keyof_T}     key
   * @return {T_U | undefined}
   */
  public static get<T, U extends keyof T>(x: T | Nullish, key: U): T[U] | undefined {
    return x ? x[key] : undefined;
  }

  /**
   * Typed, null-safe version of Object.keys:
   * > Returns the names of the enumerable string properties and methods of an object.
   * @param {Nullable<T>} x Object
   * @return {keyof_T[]} array of keys
   */
  public static keys<T>(x: Nullable<T>): (keyof T)[] {
    if (x == null) return [];
    return Object.keys(x as any) as (keyof T)[];
  }

  /**
   * Typed, null-safe version of "for in" loop of object
   * @param {T}        x
   * @param {function} callback
   */
  public static forEach<T>(x: T | Nullish, callback: (key: keyof T, value: T[keyof T]) => void): void {
    if (x == null) return;
    for (const key in x as T) callback(key, x[key]);
  }

  /**
   * Returns nested map values for selected top-level keys.
   * @param {Object | undefined} object source object grouped by id
   * @param {string[]}           keys   selected top-level keys
   * @param {Function}           filter optional value filter
   * @return {T[]} selected nested values
   */
  public static nestedValuesByKeys<T>(
    object: { [id: string]: { [id: string]: T } } | Nullish,
    keys: string[],
    filter?: (value: T) => boolean
  ): T[] {
    const data = [...new Set(keys)].flatMap((key) => Object.values(object?.[key] ?? {}));
    return filter ? data.filter(filter) : data;
  }

  /**
   * Returns map values for selected keys.
   * @param {Object | undefined} object source object
   * @param {string[]}           keys   selected keys
   * @return {T[]} selected values
   */
  public static valuesByKeys<T>(object: { [id: string]: T } | Nullish, keys: string[]): T[] {
    return [...new Set(keys)].map((key) => object?.[key]).filter((value): value is T => value != null);
  }

  /**
   * Checks if object has value - returns false for nullish, empty string, empty object and array but true for 0
   * @param {any} x to check
   * @return {boolean}
   */
  public static haveValue<T>(x: Nullable<T>): boolean {
    return typeof x == 'number' || !ObjHlp.isEmpty(x);
  }

  /**
   * Checks if any given field exist and has value in object
   * @param {any}      obj
   * @param {string[]} paths list of given keys
   * @return {boolean}
   */
  public static haveAny(obj: any, paths: string[]): boolean {
    if (!obj || ObjHlp.isEmpty(obj)) return false;
    for (const path of paths) {
      if (ObjHlp.haveValue(get(obj, path))) return true;
    }
    return false;
  }

  /**
   * Checks if all given field exist and has value in object
   * @param {any}      obj
   * @param {string[]} paths list of given keys (if empty function returns false)
   * @return {boolean}
   */
  public static haveAll(obj: any, paths: string[]): boolean {
    if (!obj || ObjHlp.isEmpty(obj) || paths.length == 0) return false;
    for (const path of paths) {
      if (!ObjHlp.haveValue(get(obj, path))) return false;
    }
    return true;
  }

  /**
   * Replaces value in a COPY of specified object field or DELETES field for undefined value
   * @param {any}    obj     object to mutate
   * @param {string} key     key of object to replace value
   * @param {any}    replace value to replace with
   * @return {any} mutated copy of object
   */
  private static replaceKey(obj: any, key: string, replace?: any): any {
    const copy = cloneDeep(obj);
    if (replace === undefined) delete copy[key];
    else copy[key] = replace!;
    return copy;
  }

  /**
   * Replaces (or inserts) value to specified fields in copy of object, for undefined DELETES fields instead
   * @param {T}        obj     object to mutate
   * @param {string[]} keys    array of keys to be replaced
   * @param {any}      replace value to replace with, no value means fields will be deleted
   * @return {T} mutated copy of object
   */
  public static replaceKeys<T>(obj: T, keys: string[], replace?: any): T {
    let copy = cloneDeep(obj);
    for (const key of keys) {
      copy = ObjHlp.replaceKey(copy, key, replace);
    }
    return copy;
  }

  /**
   * Replaces specified values recursively with new value, or deletes them if new value unspecified
   * @param {T}     obj     object to mutate
   * @param {any[]} values  array of values to be replaced
   * @param {any?}  replace value to replace with, no value means fields will be deleted
   * @return {T} mutated copy of object
   */
  public static replaceValues<T>(obj: T, values: string[], replace?: any): T;

  /**
   * Replaces fields meeting predicate recursively with new value or deletes them if new value unspecified
   * @param {T}        obj       object to mutate
   * @param {function} predicate function deciding if field will be replaced
   * @param {any?}     replace   value to replace with, no value means fields will be deleted
   * @return {T} mutated copy of object
   */
  public static replaceValues<T>(obj: T, predicate: (obj: any, key: string) => boolean, replace?: any): T;

  // Implementation
  // eslint-disable-next-line require-jsdoc
  public static replaceValues<T>(obj: T, discriminator: ((obj: any, key: string) => boolean) | string[], replace?: any): T {
    let copy = cloneDeep(obj);
    const removedKeys: string[] = [];

    // ? converting list of values to function
    if (Array.isArray(discriminator)) {
      const arr = [...discriminator]; // ? Copy value of array
      discriminator = (x: any, key: string) => arr.includes(x[key]);
    }

    for (const key in copy) {
      if (discriminator(copy, key)) {
        copy = ObjHlp.replaceKey(copy, key, replace);
      } else if (Array.isArray(copy[key])) {
        // HACK because replaceValues do not work correctly on arrays, array is mapped to object, processed,
        // HACK and mapped back to array. It's ugly as hell, but works for now
        const objFromArr = Object.fromEntries((copy[key] as any).map((v: any, i: number) => [i, v]));
        copy[key] = Object.values(ObjHlp.replaceValues(objFromArr, discriminator, replace)) as any;
      } else if (typeof copy[key] === 'object' && copy[key] != null) {
        copy[key] = ObjHlp.replaceValues(copy[key], discriminator, replace);
      }
      if (replace === undefined) removedKeys.push(key);
    }
    // if (removedKeys.length) console.warn(`[ObjHlp] Removed keys: ${removedKeys.join(', ')}`);
    return copy;
  }

  /**
   * Deletes empty string or replaces with null recursively (used before pushing data to db)
   * @param {T}             obj
   * @param {string | null} replace value replacing empty strings, if unspecified fields will be deleted
   * @return {T} copy of mutated object
   */
  public static replaceEmptyStr<T>(obj: T, replace?: string | null): T {
    return ObjHlp.replaceValues(obj, (x, k) => typeof x[k] === 'string' && x[k].length == 0, replace);
  }

  /**
   * Replaces any nullish values with null recursively (used before pushing data to db)
   * @param {T}                obj
   * @param {null | undefined} replace defines, if fields will be deleted, or replaced by null
   * @return {T} copy of mutated object
   */
  public static replaceNullish<T>(obj: T, replace?: null): T {
    return ObjHlp.replaceValues(obj, (x, k) => x[k] == null, replace);
  }

  /**
   * Deletes all nullish (null, undefined, empty string, empty array) elements from object. For non-objects returns itself
   * @param {T} obj
   * @return {Partial<T>} COPY of object with removed fields
   */
  public static deleteNullish<T>(obj: T): Partial<T> {
    if (typeof obj !== 'object' || obj === null) return obj;

    let copy = cloneDeep(obj);
    for (const key in copy) {
      const value = copy[key];
      if (typeof value === 'object' && value !== null) {
        copy[key] = ObjHlp.deleteNullish(value) as any;
      } else if (ObjHlp.isNullish(value) || ((Array.isArray(value) || typeof value === 'string') && value.length == 0)) {
        delete copy[key];
      }
    }
    return copy;
  }

  /**
   * Sanitize object before sending to database, which means replace all empty strings and nullish values with null It's
   * replaceEmptyStr and replaceNullish together
   * @param {T} obj
   * @return {T} copy of sanitized object
   */
  public static sanitizeForDB<T>(obj: T): T {
    return ObjHlp.replaceValues(obj, (x, k) => x[k] == null || (typeof x[k] === 'string' && x[k].length == 0), null);
  }

  /**
   * Compares two objects of the same type, internal objects will be compared by reference
   * @param {T} a
   * @param {T} b
   * @return {boolean} true if objects are equal, otherwise false
   */
  public static shallowCompare<T extends object>(a: T, b: T): boolean {
    // * If (for some reason) primitives are inserted, compare values
    if (ObjHlp.isPrimitiveOrNullish(a) || ObjHlp.isPrimitiveOrNullish(b)) return a === b;

    const checkedKeys: string[] = [];
    for (const key in a) {
      // ! omit null == undefined comparison
      if (a[key] == null && b[key] == null) {
        continue;
      } else if (a[key] !== b[key]) {
        return false;
      }
      checkedKeys.push(key);
    }
    for (const key in b) {
      if (b[key] != null && !checkedKeys.includes(key)) return false;
    }
    return true;
  }

  /**
   * Compares two objects of the same type recursively
   * @param {T} a
   * @param {T} b
   * @return {boolean} true if objects are equal, otherwise false
   */
  public static deepCompare<T>(a: T, b: T): boolean {
    // * If primitives are inserted, compare values
    if (ObjHlp.isPrimitiveOrNullish(a) || ObjHlp.isPrimitiveOrNullish(b)) return a === b;

    const checkedKeys: string[] = [];
    for (const key in a) {
      // ! omit null == undefined comparison
      if (a[key] == null && b[key] == null) {
        continue;
      } else if (typeof a[key] === 'object') {
        // ? recursive object checking
        if (typeof b[key] !== 'object' || !ObjHlp.deepCompare(a[key], b[key])) {
          return false;
        }
      } else if (a[key] !== b[key]) {
        return false;
      }
      checkedKeys.push(key);
    }
    for (const key in b) {
      if (b[key] != null && !checkedKeys.includes(key)) return false;
    }
    return true;
  }

  /**
   * Creates object that contains properties of two given objects
   * @param {A} a
   * @param {B} b
   * @return {A & B}
   */
  public static merge<A, B>(a: A, b: B): A & B;

  /**
   * Creates object that contains properties of three given objects
   * @param {A} a
   * @param {B} b
   * @param {C} c
   * @return {A & B & C}
   */
  public static merge<A, B, C>(a: A, b: B, c: C): A & B & C;

  /**
   * Creates object that contains properties of four given objects
   * @param {A} a
   * @param {B} b
   * @param {C} c
   * @param {D} d
   * @return {A & B & C & D}
   */
  public static merge<A, B, C, D>(a: A, b: B, c: C, d: D): A & B & C & D;

  /**
   * Creates object that contains properties of five given objects
   * @param {A} a
   * @param {B} b
   * @param {C} c
   * @param {D} d
   * @param {E} e
   * @return {A & B & C & D & E}
   */
  public static merge<A, B, C, D, E>(a: A, b: B, c: C, d: D, e: E): A & B & C & D & E;

  /**
   * Creates object that contains properties of any given objects
   * @param {any[]} objects
   * @return {any}
   */
  public static merge(...objects: any[]): any;

  // Implementation
  // eslint-disable-next-line require-jsdoc
  public static merge(...objects: any[]): any {
    const mergeProps = (target: any, source: any) => {
      for (const key in source) {
        if (typeof source[key] == 'object') {
          if (source[key] == null || typeof target[key] != 'object') target[key] = {};
          mergeProps(target[key], source[key]);
        } else {
          target[key] = source[key];
        }
      }
    };

    const res: any = {};
    for (const obj of objects) {
      mergeProps(res, obj);
    }
    return res;
  }

  /**
   * Compare two values basing on string value returned by parser
   * @param {T}        a
   * @param {T}        b
   * @param {function} parser
   * @return {number}
   */
  public static cmpStr<T>(a: T, b: T, parser: (x: T) => string): number {
    if (ObjHlp.isPrimitiveOrNullish(a) || ObjHlp.isPrimitiveOrNullish(b)) return a === b ? 0 : 1;
    return parser(a).localeCompare(parser(b));
  }

  /**
   * Checks whether a value is a non-null, non-array record.
   * @param {unknown} value Value to check
   * @return {boolean} Whether the value is a record
   */
  public static isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  /**
   * Checks whether a value is an non-null object
   * @param {unknown} value Value to check
   * @return {boolean} Whether the value is an object
   */
  public static isObject(value: unknown): value is object {
    return typeof value === 'object' && value !== null && value.constructor === Object;
  }

  /**
   * Check if objects is empty (has any properties)
   * @param {any} obj
   * @return {boolean} true if empty
   */
  public static isEmpty(obj: any): boolean {
    return !ObjHlp.keys(obj).length;
  }

  /**
   * Checks if object is primitive (string, number, boolean, bigint and symbol)
   * bigint is used in es2020+
   * @param {any} obj
   * @return {boolean} true if primitive
   */
  public static isPrimitive(obj: any): boolean {
    const type = typeof obj;
    return type == 'string' || type == 'number' || type == 'boolean' || type == 'bigint' || type == 'symbol';
  }

  /**
   * Check if object is nullish (undefined or null)
   * @param {any} obj
   * @return {boolean}
   */
  public static isNullish(obj: any): boolean {
    return obj === undefined || obj === null;
  }

  /**
   * Check if object is primitive (string, number, boolean, bigint and symbol) or nullish (undefined or null)
   * @param {any} obj
   * @return {boolean}
   */
  public static isPrimitiveOrNullish(obj: any): boolean {
    return ObjHlp.isPrimitive(obj) || ObjHlp.isNullish(obj);
  }

  /**
   * Convert multi-level objects like 'name/{id1}/{id2}: T to list of T; /
   * For depth = 1 works like Object.values
   * @param {any}      obj   object to flatten
   * @param {number}   depth search depth (default 2)
   * @param {function} cond  (optional) conditional function for last depth of tree; it decides if object in last depth should be
   *                         added to result array
   * @return {T[]} list of all (filtered by condition) objects
   */
  public static flatten<T>(obj: any, depth = 2, cond?: (key: string, value: T) => boolean): T[] {
    if (ObjHlp.isNullish(obj) || depth < 1) return [];
    if (ObjHlp.isPrimitive(obj)) return [obj];

    // ? 'list' object => list
    const array: T[] = [];
    if (depth == 1) {
      Object.entries(obj).forEach((v) => {
        const value = v[1] as T;
        if (!cond || cond(v[0], value)) array.push(value);
      });
    } else {
      const entries: [string, any][] = Object.entries(obj);
      entries.forEach((entry) => {
        array.push(...this.flatten<T>(entry[1], depth - 1, cond));
      });
    }

    return array;
  }
}
